import { Children, cloneElement, PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { Button, ButtonProps, ClickAwayListener, Grow, MenuList, Paper, Popper } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

type PopUpMenuProps = {
  buttonProps: ButtonProps;
  menuText: string;
};

// Based off of https://mui.com/material-ui/react-menu/
export const PopUpMenu = ({ buttonProps, menuText, children }: PropsWithChildren<PopUpMenuProps>) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleClose = (event?: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((previousOpen) => !previousOpen)}
        endIcon={<KeyboardArrowDown />}
        {...buttonProps}
      >
        {menuText}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ zIndex: 2 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open}>
                  {children
                    ? Children.toArray(children)
                        .filter((child): child is ReactElement => child !== null)
                        .map((child) =>
                          cloneElement(child, {
                            onClick: () => {
                              handleClose();
                              if (child.props.onClick) {
                                child.props.onClick();
                              }
                            },
                          }),
                        )
                    : null}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
